// MenuPanel: the overlay every list and menu in the kit opens in. Anchored to its trigger or to
// the point that was pressed, measured so it always fits the window, scaling in from where it was
// opened and fading out the way it came. Select, Menu and ContextMenu draw through it; a menu
// that turns into a small form in place can too, with the row recipes below.
// docs/operations/design-standard.md, "Controls".

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MS, usePresence } from './motion';

/** A trigger to hang from, or the client point a right-click landed on. */
export type MenuAnchor = React.RefObject<HTMLElement | null> | { x: number; y: number };

export interface MenuPanelProps {
  open: boolean;
  anchor: MenuAnchor;
  /**
   * Closed from outside: a press elsewhere, focus moving away, a scroll or a resize. `refocus`
   * is true when focus was inside the panel and nothing else took it (a scroll or a resize).
   */
  onDismiss: (refocus: boolean) => void;
  role: 'menu' | 'listbox' | 'dialog';
  label: string;
  id?: string;
  'aria-describedby'?: string;
  /** Takes the trigger's width (180 to 320px): a selector's list. Otherwise the content sets it. */
  matchWidth?: boolean;
  /** The trigger edge a content-width panel lines up with. It swaps when that side has no room. */
  align?: 'start' | 'end';
  maxHeight?: number;
  /** Change it to re-measure while open, when the content changes shape. */
  layoutKey?: unknown;
  /** In place and visible: the moment to move focus in. */
  onOpened?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  className?: string;
  children: React.ReactNode;
}

interface Place { left: number; top?: number; bottom?: number; width?: number; maxHeight: number; origin: string }

const EDGE = 8;
const GAP = 4;

const isPoint = (a: MenuAnchor): a is { x: number; y: number } => 'x' in a;

// Above every modal in the app (the highest sits at z-[1200]): a list opened from inside a dialog
// has to land on top of it, the way the OS list of a native select always did.
const LAYER = 'z-[1300]';

export const MenuPanel = React.forwardRef<HTMLDivElement, MenuPanelProps>(({
  open,
  anchor,
  onDismiss,
  role,
  label,
  id,
  'aria-describedby': describedBy,
  matchWidth,
  align = 'start',
  maxHeight,
  layoutKey,
  onOpened,
  onKeyDown,
  onBlur,
  className = '',
  children,
}, ref) => {
  const presence = usePresence(open, MS.exit);
  const [place, setPlace] = useState<Place | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const anchorNow = useRef(anchor);
  anchorNow.current = anchor;
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  const opened = useRef(onOpened);
  opened.current = onOpened;

  const setRef = useCallback((el: HTMLDivElement | null) => {
    panel.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
  }, [ref]);

  const point = isPoint(anchor) ? `${anchor.x},${anchor.y}` : '';

  useLayoutEffect(() => {
    if (!open || !presence.mounted) return;
    const el = panel.current;
    const a = anchorNow.current;
    const r = isPoint(a)
      ? { left: a.x, right: a.x, top: a.y, bottom: a.y, width: 0 }
      : a.current?.getBoundingClientRect();
    if (!el || !r) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = isPoint(a) ? 0 : GAP;
    const width = matchWidth ? Math.min(Math.max(r.width, 180), 320, vw - EDGE * 2) : undefined;

    // Measured at the left edge: a fixed box near the right edge is squeezed by the space beside it,
    // and would measure narrower than it lays out once moved.
    const was = { left: el.style.left, width: el.style.width };
    el.style.left = '0px';
    el.style.width = width === undefined ? '' : `${width}px`;
    const w = width ?? Math.min(el.offsetWidth, vw - EDGE * 2);
    const h = el.scrollHeight;
    el.style.left = was.left;
    el.style.width = was.width;

    const below = vh - r.bottom - gap - EDGE;
    const above = r.top - gap - EDGE;
    const cap = maxHeight ?? (matchWidth ? 320 : Math.round(vh * 0.7));
    const up = Math.min(h, cap) > below && above > below;
    const room = Math.max(0, Math.min(cap, up ? above : below));

    let left: number;
    if (isPoint(a)) {
      left = r.left + w + EDGE > vw ? r.left - w : r.left;
    } else {
      const start = r.left;
      const end = r.right - w;
      const startFits = start + w <= vw - EDGE;
      const endFits = end >= EDGE;
      left = align === 'end' ? (endFits || !startFits ? end : start) : (startFits || !endFits ? start : end);
    }
    left = Math.max(EDGE, Math.min(left, vw - w - EDGE));
    const ox = Math.round(Math.max(0, Math.min(w, r.left + r.width / 2 - left)));
    const origin = `${ox}px ${up ? '100%' : '0'}`;
    setPlace(up
      ? { left, width, bottom: vh - r.top + gap, maxHeight: room, origin }
      : { left, width, top: r.bottom + gap, maxHeight: room, origin });
  }, [open, presence.mounted, layoutKey, point]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = open && presence.mounted && place !== null;
  useEffect(() => {
    if (ready) opened.current?.();
  }, [ready]);

  useEffect(() => {
    if (!open) return undefined;
    const trigger = () => {
      const a = anchorNow.current;
      return isPoint(a) ? null : a.current;
    };
    const inside = (t: EventTarget | null) =>
      t instanceof Node && (!!panel.current?.contains(t) || !!trigger()?.contains(t));
    const press = (e: Event) => { if (!inside(e.target)) dismiss.current(false); };
    const focus = (e: FocusEvent) => { if (!inside(e.target)) dismiss.current(false); };
    const drift = (e: Event) => {
      if (e.target instanceof Node && panel.current?.contains(e.target)) return;
      dismiss.current(!!panel.current?.contains(document.activeElement));
    };
    // Capture: a canvas or a map that stops its own presses still has to close the menu.
    document.addEventListener('mousedown', press, true);
    document.addEventListener('touchstart', press, { capture: true, passive: true });
    document.addEventListener('focusin', focus);
    window.addEventListener('scroll', drift, true);
    window.addEventListener('wheel', drift, { capture: true, passive: true });
    window.addEventListener('resize', drift);
    return () => {
      document.removeEventListener('mousedown', press, true);
      document.removeEventListener('touchstart', press, true);
      document.removeEventListener('focusin', focus);
      window.removeEventListener('scroll', drift, true);
      window.removeEventListener('wheel', drift, true);
      window.removeEventListener('resize', drift);
    };
  }, [open]);

  // A leaving panel is hidden from the keyboard too: keys typed during its exit would reach rows
  // nobody can see. Focus that nothing else claimed is let go.
  useEffect(() => {
    if (open) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && panel.current?.contains(active)) active.blur();
  }, [open]);

  if (!presence.mounted || typeof document === 'undefined') return null;
  const shown = presence.open && open;
  return createPortal(
    <div
      ref={setRef}
      id={id}
      role={role}
      aria-label={label}
      aria-describedby={describedBy}
      aria-hidden={open ? undefined : true}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onContextMenu={(e) => e.preventDefault()}
      style={place
        ? { left: place.left, top: place.top, bottom: place.bottom, width: place.width, maxHeight: place.maxHeight, transformOrigin: place.origin }
        : { left: 0, top: 0, visibility: 'hidden' }}
      className={`fixed ${LAYER} overflow-y-auto overscroll-contain rounded-overlay bg-surface-container-high p-1 shadow-overlay transition-[opacity,transform] motion-reduce:transition-[opacity] ${
        shown
          ? 'scale-100 opacity-100 duration-standard ease-emphasized'
          : 'pointer-events-none scale-[0.96] opacity-0 duration-exit ease-exit'
      } motion-reduce:scale-100 ${className}`}
    >
      {children}
    </div>,
    document.body,
  );
});
MenuPanel.displayName = 'MenuPanel';

// ── Rows ─────────────────────────────────────────────────────────────────────

// Rows grow on touch screens rather than taking an invisible margin: neighbouring rows would
// share it, and a tap on the seam would land on the wrong one.
const ROW = 'flex w-full min-w-0 min-h-8 items-center gap-2 rounded-inner px-2 py-1 text-left text-sm transition-[background-color,color] duration-quick ease-emphasized focus:outline-none [@media(pointer:coarse)]:min-h-11';

const OFF = 'cursor-pointer disabled:cursor-default disabled:text-on-surface-disabled disabled:hover:bg-transparent aria-disabled:cursor-default aria-disabled:text-on-surface-disabled aria-disabled:hover:bg-transparent aria-disabled:focus:bg-transparent';

const TONE = {
  plain: 'text-on-surface hover:bg-surface-container-highest focus:bg-surface-container-highest',
  danger: 'text-red-300 hover:bg-red-500/15 focus:bg-red-500/15',
};

/** One row of a list or menu. The current row (hovered or keyboard-focused) steps up a tone. */
export const menuRow = (tone: 'plain' | 'danger' = 'plain'): string => `${ROW} ${TONE[tone]} ${OFF}`;

/** A group's name inside a list: sentence case, quieter than the rows it names. */
export const MENU_HEADING = 'px-2 pb-1 pt-1.5 text-xs font-medium text-on-surface-variant';

/** The rule before a group with no name, such as the destructive tier at the end. */
export const MENU_RULE = 'mx-2 my-1 h-px bg-outline-variant';

/**
 * The rows the keyboard can land on, in order: elements marked `data-row`, skipping disabled
 * ones. `data-label` on a row is what typeahead matches.
 */
export const rowsIn = (root: HTMLElement | null): HTMLElement[] =>
  root ? Array.from(root.querySelectorAll<HTMLElement>('[data-row]:not(:disabled):not([aria-disabled="true"])')) : [];

const typing = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);

/** Arrows (wrapping, both axes), Home and End over the rows. True when it took the key. */
export function stepRows(e: React.KeyboardEvent, rows: HTMLElement[]): boolean {
  if (typing(e.target)) return false;
  const n = rows.length;
  const at = rows.indexOf(document.activeElement as HTMLElement);
  let next: number;
  switch (e.key) {
    case 'ArrowDown': case 'ArrowRight': next = at < 0 ? 0 : (at + 1) % Math.max(n, 1); break;
    case 'ArrowUp': case 'ArrowLeft': next = at < 0 ? n - 1 : (at - 1 + n) % Math.max(n, 1); break;
    case 'Home': next = 0; break;
    case 'End': next = n - 1; break;
    default: return false;
  }
  e.preventDefault();
  rows[next]?.focus();
  return true;
}

/** A key that types a character, not a command. */
export const printable = (e: React.KeyboardEvent): boolean =>
  e.key.length === 1 && /\S/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey;

/**
 * Typeahead over rows by their `data-label`. A run of keys inside 600ms spells a prefix; the same
 * letter repeated cycles through its matches. Returns the row to focus, or null.
 */
export function useTypeahead(): (key: string, rows: HTMLElement[]) => HTMLElement | null {
  const typed = useRef({ text: '', at: 0 });
  return useCallback((key: string, rows: HTMLElement[]) => {
    const now = Date.now();
    const t = typed.current;
    t.text = now - t.at < 600 ? t.text + key : key;
    t.at = now;
    const q = t.text.toLowerCase();
    const repeat = q.split('').every((c) => c === q[0]);
    const query = repeat ? q[0] : q;
    const from = rows.indexOf(document.activeElement as HTMLElement);
    const start = from < 0 ? 0 : from + (repeat ? 1 : 0);
    for (let k = 0; k < rows.length; k += 1) {
      const row = rows[(start + k) % rows.length];
      if ((row.dataset.label || '').toLowerCase().startsWith(query)) return row;
    }
    return null;
  }, []);
}

/** The pointer moves the current row, as the keyboard does, so there is only ever one. */
export const followPointer = (e: React.MouseEvent<HTMLElement>): void => {
  const el = e.currentTarget;
  if (document.activeElement === el || el.matches(':disabled, [aria-disabled="true"]')) return;
  el.focus({ preventScroll: true });
};

export default MenuPanel;
